import { useEffect, useState, useCallback } from "react";
import { MapPin, Bell, Check, X, Loader2, ShieldCheck, AlertTriangle, RefreshCw, ChevronUp } from "lucide-react";
import { Geolocation } from "@capacitor/geolocation";
import { PushNotifications } from "@capacitor/push-notifications";
import { isNativeApp } from "../utils/platform";

// ── Platform detection ────────────────────────────────────────────────────────
const UA    = navigator.userAgent;
const isIOS = /iPhone|iPad|iPod/i.test(UA) && !window.MSStream;
const isPWA = window.matchMedia("(display-mode: standalone)").matches || !!window.navigator.standalone;

const notifSupported = "Notification" in window && (!isIOS || isPWA);

const GRANT_KEY    = "qk_perms_granted_v1";
const GRANT_DAYS   = 365;
const SNOOZE_MS    = 90_000; // reaparece 90 s después de minimizar

// ── Helpers ───────────────────────────────────────────────────────────────────
// En la app nativa (Capacitor) el permiso real lo maneja Android, no el WebView —
// navigator.permissions.query()/Notification.permission consultan el estado interno
// del WebView, que no siempre refleja el permiso nativo ya concedido (por eso la app
// seguía pidiendo ubicación aunque Android ya la tuviera permitida). Los plugins
// nativos sí reflejan el estado real del sistema operativo.
async function queryGeo() {
  if (isNativeApp) {
    try {
      const r = await Geolocation.checkPermissions();
      return r.location;
    } catch { return "prompt"; }
  }
  if (!("geolocation" in navigator)) return "unsupported";
  try {
    const r = await navigator.permissions.query({ name: "geolocation" });
    return r.state;
  } catch { return "prompt"; }
}

async function queryNotif() {
  if (isNativeApp) {
    try {
      const r = await PushNotifications.checkPermissions();
      return r.receive;
    } catch { return "prompt"; }
  }
  if (!notifSupported) return "unsupported";
  const p = Notification.permission;
  return p === "default" ? "prompt" : p;
}

function isCachedGranted() {
  try {
    const raw = localStorage.getItem(GRANT_KEY);
    if (!raw) return false;
    const { grantedAt } = JSON.parse(raw);
    return Date.now() - grantedAt < GRANT_DAYS * 86_400_000;
  } catch { return false; }
}

function saveGranted() {
  try { localStorage.setItem(GRANT_KEY, JSON.stringify({ grantedAt: Date.now() })); }
  catch {}
}

// ── Instrucciones para permisos denegados ─────────────────────────────────────
function DeniedInstructions({ type }) {
  if (type === "location") {
    if (isIOS) return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-800 space-y-1.5">
        <p className="font-bold flex items-center gap-1.5"><AlertTriangle size={12} /> Ubicación bloqueada:</p>
        <ol className="list-decimal list-inside space-y-0.5 text-red-700">
          <li>Ajustes → <strong>Privacidad → Localización</strong></li>
          <li>Busca <strong>Safari / Qkiere</strong> → <strong>Mientras se usa</strong></li>
          <li>Vuelve aquí y toca <strong>"Ya lo activé"</strong></li>
        </ol>
      </div>
    );
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-800 space-y-1.5">
        <p className="font-bold flex items-center gap-1.5"><AlertTriangle size={12} /> Ubicación bloqueada:</p>
        <ol className="list-decimal list-inside space-y-0.5 text-red-700">
          <li>Toca el <strong>candado 🔒</strong> en la barra del navegador</li>
          <li>Selecciona <strong>Permisos del sitio → Ubicación → Permitir</strong></li>
          <li>Recarga y toca <strong>"Ya lo activé"</strong></li>
        </ol>
      </div>
    );
  }
  if (isIOS) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 space-y-1.5">
      <p className="font-bold flex items-center gap-1.5"><AlertTriangle size={12} /> Notificaciones bloqueadas:</p>
      <ol className="list-decimal list-inside space-y-0.5 text-amber-700">
        <li>Ajustes → <strong>Notificaciones → Safari / Qkiere</strong></li>
        <li>Activa <strong>Permitir notificaciones</strong></li>
        <li>Vuelve aquí y toca <strong>"Ya lo activé"</strong></li>
      </ol>
    </div>
  );
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 space-y-1.5">
      <p className="font-bold flex items-center gap-1.5"><AlertTriangle size={12} /> Notificaciones bloqueadas:</p>
      <ol className="list-decimal list-inside space-y-0.5 text-amber-700">
        <li>Toca el <strong>candado 🔒</strong> en la barra del navegador</li>
        <li><strong>Permisos del sitio → Notificaciones → Permitir</strong></li>
        <li>Vuelve aquí y toca <strong>"Ya lo activé"</strong></li>
      </ol>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
// phases: 'checking' | 'banner' | 'minimized' | 'requesting' | 'done'
export default function PermissionPrompt() {
  const [phase,      setPhase]      = useState("checking");
  const [locState,   setLocState]   = useState("prompt");
  const [notifState, setNotifState] = useState("prompt");
  const [rechecking, setRechecking] = useState(false);
  const [expanded,   setExpanded]   = useState(false);

  // ── Revisar permisos reales ───────────────────────────────────────────────
  const checkPermissions = useCallback(async (showRecheck = false) => {
    if (showRecheck) setRechecking(true);
    const geo   = await queryGeo();
    const notif = await queryNotif();
    setLocState(geo);
    setNotifState(notif);
    if (showRecheck) setRechecking(false);

    const allOk = (geo   === "granted" || geo   === "unsupported")
               && (notif === "granted" || notif === "unsupported");
    if (allOk) { saveGranted(); setPhase("done"); return true; }
    return false;
  }, []);

  // ── Carga inicial ─────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      if (isCachedGranted()) {
        const ok = await checkPermissions();
        if (ok) return;
      }
      const ok = await checkPermissions();
      if (ok) return;
      // SIEMPRE mostrar primero el aviso propio (banner) y pedir el permiso
      // nativo recién cuando el usuario toca "Activar" — nunca saltar directo
      // al diálogo del sistema, ni en la app nativa. Play Store rechazó la app
      // por esto exacto ("Aviso destacado inadecuado": el diálogo nativo de
      // Android aparecía sin que la app hubiera explicado antes qué datos usa
      // y para qué — la política exige que la divulgación propia preceda
      // inmediatamente a la solicitud de permisos, no que se omita en nativo).
      setPhase("banner");
    })();
    // eslint-disable-next-line
  }, [checkPermissions]);

  // ── Auto-reaparición tras minimizar ───────────────────────────────────────
  useEffect(() => {
    if (phase !== "minimized") return;
    const t = setTimeout(() => { setPhase("banner"); setExpanded(false); }, SNOOZE_MS);
    return () => clearTimeout(t);
  }, [phase]);

  // ── Re-verificar al recuperar foco ────────────────────────────────────────
  useEffect(() => {
    const onFocus = () => {
      if (phase === "banner" || phase === "minimized") checkPermissions(true);
    };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [phase, checkPermissions]);

  // ── Escuchar cambios del permiso de ubicación ─────────────────────────────
  useEffect(() => {
    let watcher = null;
    navigator.permissions?.query({ name: "geolocation" }).then(s => {
      watcher = s;
      s.onchange = () => { if (phase === "banner") checkPermissions(true); };
    }).catch(() => {});
    return () => { if (watcher) watcher.onchange = null; };
  }, [phase, checkPermissions]);

  // ── Solicitar permisos ────────────────────────────────────────────────────
  const handleRequest = async () => {
    setPhase("requesting");
    setLocState("requesting");

    let geoResult = "denied";
    if (isNativeApp) {
      try {
        const r = await Geolocation.requestPermissions();
        geoResult = r.location === "granted" ? "granted" : "denied";
      } catch { geoResult = "denied"; }
    } else {
      try {
        await new Promise((res, rej) =>
          navigator.geolocation.getCurrentPosition(res, rej, { timeout: 15000, enableHighAccuracy: false })
        );
        geoResult = "granted";
      } catch { geoResult = "denied"; }
    }
    setLocState(geoResult);

    let notifResult = notifSupported ? "prompt" : "unsupported";
    if (isNativeApp) {
      setNotifState("requesting");
      try {
        const r = await PushNotifications.requestPermissions();
        notifResult = r.receive === "granted" ? "granted" : "denied";
      } catch { notifResult = "denied"; }
      setNotifState(notifResult);
    } else if (notifSupported && Notification.permission !== "denied") {
      setNotifState("requesting");
      try {
        const p = await Notification.requestPermission();
        notifResult = p === "granted" ? "granted" : "denied";
      } catch { notifResult = "denied"; }
      setNotifState(notifResult);
    } else {
      notifResult = notifSupported ? "denied" : "unsupported";
    }

    const allOk = geoResult === "granted"
               && (notifResult === "granted" || notifResult === "unsupported");
    if (allOk) { saveGranted(); setPhase("done"); }
    else { setPhase("banner"); setExpanded(true); }
  };

  // ── No mostrar ────────────────────────────────────────────────────────────
  if (phase === "checking" || phase === "done") return null;

  const locDenied    = locState   === "denied";
  const notifDenied  = notifState === "denied";
  const anyDenied    = locDenied || notifDenied;
  const isRequesting = phase === "requesting";
  const isMinimized  = phase === "minimized";

  // ── Pill minimizada ───────────────────────────────────────────────────────
  if (isMinimized) {
    return (
      <button
        onClick={() => { setPhase("banner"); setExpanded(false); }}
        className="fixed bottom-20 left-4 z-50 flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold px-3 py-2 rounded-full shadow-lg transition active:scale-95"
        style={{ animation: "perm-fadein 0.25s ease" }}
      >
        <ShieldCheck size={14} />
        Permisos pendientes
        <ChevronUp size={13} />
        <style>{`@keyframes perm-fadein { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }`}</style>
      </button>
    );
  }

  // ── Banner flotante (no bloqueante) ───────────────────────────────────────
  return (
    <div
      className="fixed bottom-20 sm:bottom-4 left-1/2 z-50 w-full max-w-sm px-3"
      style={{ transform: "translateX(-50%)", animation: "perm-slidein 0.3s cubic-bezier(0.32,0.72,0,1)" }}
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">

        {/* ── Cabecera compacta siempre visible ── */}
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="w-8 h-8 bg-amber-100 rounded-xl flex items-center justify-center shrink-0">
            <ShieldCheck size={16} className="text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-800 leading-tight">Activa los permisos</p>
            <p className="text-xs text-gray-500 leading-snug">
              Qkiere usa tu <strong>ubicación</strong> para calcular la distancia y el tiempo de entrega
              de tus pedidos, y te envía <strong>notificaciones</strong> sobre su estado. Solo mientras
              usas la app — nunca se comparte con terceros.
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {/* Expandir / contraer */}
            <button
              onClick={() => setExpanded(v => !v)}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
            >
              <ChevronUp
                size={15}
                className="transition-transform"
                style={{ transform: expanded ? "rotate(0deg)" : "rotate(180deg)" }}
              />
            </button>
            {/* Minimizar (snooze 90s) */}
            <button
              onClick={() => setPhase("minimized")}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {/* ── Indicadores de estado rápidos ── */}
        <div className="flex gap-2 px-4 pb-3">
          {/* Ubicación */}
          <div className={`flex items-center gap-1.5 flex-1 px-2.5 py-1.5 rounded-xl text-xs font-semibold ${
            locState === "granted"    ? "bg-green-50  text-green-700"  :
            locState === "denied"     ? "bg-red-50    text-red-700"    :
            locState === "requesting" ? "bg-blue-50   text-blue-700"   :
            "bg-gray-50 text-gray-500"
          }`}>
            {locState === "requesting"
              ? <Loader2 size={11} className="animate-spin shrink-0" />
              : locState === "granted"
              ? <Check size={11} className="shrink-0" />
              : <MapPin size={11} className="shrink-0" />}
            Ubicación
          </div>
          {/* Notificaciones */}
          <div className={`flex items-center gap-1.5 flex-1 px-2.5 py-1.5 rounded-xl text-xs font-semibold ${
            notifState === "granted" || notifState === "unsupported"
                                          ? "bg-green-50  text-green-700"  :
            notifState === "denied"     ? "bg-amber-50 text-amber-700"   :
            notifState === "requesting" ? "bg-blue-50   text-blue-700"   :
            "bg-gray-50 text-gray-500"
          }`}>
            {notifState === "requesting"
              ? <Loader2 size={11} className="animate-spin shrink-0" />
              : (notifState === "granted" || notifState === "unsupported")
              ? <Check size={11} className="shrink-0" />
              : <Bell size={11} className="shrink-0" />}
            Notificaciones
          </div>
        </div>

        {/* ── Contenido expandido ── */}
        {expanded && (
          <div className="border-t border-gray-100 px-4 py-3 space-y-3">

            {/* Instrucciones si hay permisos denegados */}
            {locDenied   && <DeniedInstructions type="location" />}
            {notifDenied && <DeniedInstructions type="notifications" />}

            {/* iOS PWA hint */}
            {isIOS && !isPWA && notifState !== "unsupported" && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 flex items-start gap-2">
                <Bell size={11} className="text-amber-500 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-800">
                  <strong>iPhone:</strong> Toca <strong>Compartir → Añadir a inicio</strong> para habilitar notificaciones.
                </p>
              </div>
            )}

            {rechecking && (
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Loader2 size={11} className="animate-spin" /> Verificando permisos…
              </div>
            )}

            {/* CTA */}
            {!anyDenied ? (
              <button
                onClick={handleRequest}
                disabled={isRequesting}
                className="w-full py-2.5 bg-supernova-500 hover:bg-supernova-600 active:scale-[0.98] text-white font-bold rounded-xl text-sm transition flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {isRequesting
                  ? <><Loader2 size={15} className="animate-spin" /> Activando…</>
                  : <><ShieldCheck size={15} /> Activar permisos</>}
              </button>
            ) : (
              <button
                onClick={() => checkPermissions(true)}
                disabled={rechecking}
                className="w-full py-2.5 bg-gray-800 hover:bg-gray-900 active:scale-[0.98] text-white font-bold rounded-xl text-sm transition flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {rechecking
                  ? <><Loader2 size={15} className="animate-spin" /> Verificando…</>
                  : <><RefreshCw size={15} /> Ya lo activé, verificar</>}
              </button>
            )}

            <p className="text-center text-[10px] text-gray-400 leading-relaxed">
              Puedes seguir navegando. Tus datos de ubicación nunca se comparten con terceros.
            </p>
          </div>
        )}

        {/* ── Botón rápido si no está expandido ── */}
        {!expanded && (
          <div className="px-4 pb-3">
            {!anyDenied ? (
              <button
                onClick={handleRequest}
                disabled={isRequesting}
                className="w-full py-2 bg-supernova-500 hover:bg-supernova-600 active:scale-[0.98] text-white font-bold rounded-xl text-xs transition flex items-center justify-center gap-1.5 disabled:opacity-60"
              >
                {isRequesting
                  ? <><Loader2 size={13} className="animate-spin" /> Activando…</>
                  : <><ShieldCheck size={13} /> Activar permisos</>}
              </button>
            ) : (
              <button
                onClick={() => { setExpanded(true); checkPermissions(true); }}
                disabled={rechecking}
                className="w-full py-2 bg-gray-700 hover:bg-gray-800 active:scale-[0.98] text-white font-bold rounded-xl text-xs transition flex items-center justify-center gap-1.5 disabled:opacity-60"
              >
                {rechecking
                  ? <><Loader2 size={13} className="animate-spin" /> Verificando…</>
                  : <><RefreshCw size={13} /> Ya los activé, verificar</>}
              </button>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes perm-slidein {
          from { opacity: 0; transform: translateX(-50%) translateY(16px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes perm-fadein {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
